import { useEffect, useRef } from 'react';

import heroMapRaw from './assets/hero-map.svg?raw';
import { HERO_DISTRICT_IDS, HERO_DISTRICT_TO_REGION } from './districts';

/**
 * RegionOutlineMap — spec §15, §25.2. The white outline map on the hero's right.
 *
 * Distinct from the interactive choropleth (SjRegionMap): this is a display map over the
 * dark hero photo. The `highlightRegion` region's districts fill solid white; every other
 * district is a white outline (fill transparent). The asset's own default fills are the
 * mockup's snapshot, so this normalises every district to a known state on mount and then
 * reflects the active region — nothing is left to the asset's incidental defaults.
 *
 * Display-only by default; `onRegionClick` is optional so the hero could make districts
 * clickable, though the numbered stepper is the primary control there.
 */

export type RegionOutlineMapProps = {
  highlightRegion?: string | null;
  onRegionClick?: (regionSlug: string) => void;
  className?: string;
};

const ACTIVE_FILL = '#FFFFFF';
const STROKE = '#BABABA';

export function RegionOutlineMap({
  highlightRegion = null,
  onRegionClick,
  className = '',
}: RegionOutlineMapProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  const clickRef = useRef(onRegionClick);
  clickRef.current = onRegionClick;

  // Normalise every district to a clean outline once, and wire optional clicks.
  useEffect(() => {
    const svg = containerRef.current?.querySelector('svg');
    if (!svg) return;

    svg.removeAttribute('width');
    svg.removeAttribute('height');
    svg.setAttribute('class', 'h-full w-full');
    svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');

    const cleanups: Array<() => void> = [];

    for (const id of HERO_DISTRICT_IDS) {
      const path = svg.querySelector<SVGPathElement>(`#${CSS.escape(id)}`);
      if (!path) continue;

      path.style.stroke = STROKE;
      path.style.strokeWidth = '4';
      path.style.transition = 'fill 200ms ease';

      if (clickRef.current) {
        path.style.cursor = 'pointer';
        const region = HERO_DISTRICT_TO_REGION[id];
        const onClick = () => clickRef.current?.(region);
        path.addEventListener('click', onClick);
        cleanups.push(() => path.removeEventListener('click', onClick));
      }
    }

    return () => cleanups.forEach((fn) => fn());
  }, []);

  // Reflect the active region: its districts fill white, the rest go transparent.
  useEffect(() => {
    const svg = containerRef.current?.querySelector('svg');
    if (!svg) return;

    for (const id of HERO_DISTRICT_IDS) {
      const path = svg.querySelector<SVGPathElement>(`#${CSS.escape(id)}`);
      if (!path) continue;
      const active = HERO_DISTRICT_TO_REGION[id] === highlightRegion;
      path.style.fill = active ? ACTIVE_FILL : 'transparent';
    }
  }, [highlightRegion]);

  return (
    <div
      ref={containerRef}
      className={className}
      dangerouslySetInnerHTML={{ __html: heroMapRaw }}
      role="img"
      aria-label="Outline map of Samtskhe-Javakheti"
    />
  );
}
